import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router-dom'
import React, { useEffect } from 'react'
import { Alert, Button, Col, Image, Input, Row, Space, Tag } from 'antd'
import {
    StarOutlined,
    EditOutlined,
    MessageOutlined,
    InfoCircleOutlined,
    CheckOutlined,
    CloseOutlined,
    UserOutlined,
    ShoppingCartOutlined,
    ClockCircleOutlined,
    MobileOutlined,
    SafetyCertificateOutlined,
} from '@ant-design/icons'
import controller from './reviews.controller'
import { RatingStars, entityLink, statusColor, typeIcon } from './Reviews'
import i from '../../../translations/i'
import { Titles } from '../../../layouts/Titles'
import { SectionBadge, FieldBlock, PlainField } from '../../../components/SectionCard'

const Photos = ({ files }: { files: any[] }) => {
    if (!files || files.length === 0) return null

    return (
        <Image.PreviewGroup>
            <Space wrap size={8}>
                {files.map((file: any) => (
                    <Image
                        key={file.id}
                        src={file.url}
                        width={96}
                        height={96}
                        style={{ objectFit: 'cover', borderRadius: 10 }}
                    />
                ))}
            </Space>
        </Image.PreviewGroup>
    )
}

const MetaRow = ({ icon, label, children }: any) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, width: '100%' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary-color)', minWidth: 150, display: 'flex', alignItems: 'center', gap: 6 }}>
            {icon}{label}
        </span>
        <span style={{ fontSize: 13, color: 'var(--text-color)' }}>{children}</span>
    </div>
)

const formatDateTime = (value: string | null) => value
    ? new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value))
    : '—'

export const ReviewPage = observer(() => {
    // @ts-ignore
    const { reviewID } = useParams()

    useEffect(() => {
        controller.getOne(reviewID)
    }, [reviewID])

    const review = controller.review

    const breadcrumb = [
        { title: i.t('reviews:title'), href: '/shop/reviews' },
        { title: `${i.t('reviews:titleDetail')} #${reviewID}` },
    ]

    if (!review) {
        return <Titles title={i.t('reviews:titleDetail')} breadcrumb={breadcrumb} />
    }

    const link = entityLink(review)
    // Если у отзыва висит правка, модератор решает судьбу именно её
    const revision = review.pendingRevision

    // Одобрение и отклонение живут только в блоке модерации: причина отклонения
    // вводится там же, иначе из шапки легко отправить пустую
    const buttons = [
        { title: i.t('def:actions.backList'), href: '/shop/reviews', type: 'dashed' },
        { title: i.t('def:actions.remove'), click: () => controller.remove(Number(reviewID), true), type: 'dashed', danger: true },
    ]

    return (
        <>
            <Titles
                title={`${i.t('reviews:titleDetail')} #${review.id}`}
                buttons={buttons}
                breadcrumb={breadcrumb}
            />

            <Row gutter={[20, 20]}>
                <Col span={24} xl={14}>
                    <div className="order__block" style={{ width: '100%', height: 'auto', alignSelf: 'flex-start' }}>
                        <SectionBadge
                            icon={<StarOutlined />}
                            text={i.t('reviews:titleDetail')}
                            background="linear-gradient(135deg, var(--primary-color), #9186e0)"
                            toggle={<Tag color={statusColor(review.status)} style={{ margin: 0 }}>{i.t(`reviews:statuses.${review.status}`)}</Tag>}
                        />

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
                            <Space size={6} wrap>
                                <Tag icon={typeIcon(review.entityType)} style={{ margin: 0 }}>
                                    {i.t(`reviews:types.${review.entityType}`)}
                                </Tag>
                                {link && <a href={link} target="_blank" rel="noreferrer">#{review.entityId}</a>}
                                {review.isVerifiedPurchase && (
                                    <Tag color="blue" icon={<SafetyCertificateOutlined />} style={{ margin: 0 }}>
                                        {i.t('reviews:fields.verified')}
                                    </Tag>
                                )}
                            </Space>

                            <RatingStars value={review.rating} size={22} />

                            <div style={{ fontSize: 15, lineHeight: 1.6, color: review.comment ? 'var(--text-color)' : 'var(--text-placeholder-color)' }}>
                                {review.comment ?? i.t('reviews:noComment')}
                            </div>

                            <Photos files={review.files} />

                            {review.rejectReason && (
                                <Alert
                                    type="error"
                                    showIcon
                                    message={`${i.t('reviews:fields.rejectReason')}: ${review.rejectReason}`}
                                />
                            )}
                        </div>
                    </div>
                </Col>

                <Col span={24} xl={10}>
                    <div className="order__block" style={{ width: '100%', height: 'auto', alignSelf: 'flex-start' }}>
                        <SectionBadge
                            icon={<InfoCircleOutlined />}
                            text={i.t('def:information')}
                            background="linear-gradient(135deg, #3f3d56, #63607f)"
                        />

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
                            <MetaRow icon={<UserOutlined />} label={i.t('reviews:fields.user')}>
                                <a href={`/users/${review.userId}`} target="_blank" rel="noreferrer">
                                    {review.userName ?? review.userId}
                                </a>
                            </MetaRow>

                            {review.orderId && (
                                <MetaRow icon={<ShoppingCartOutlined />} label={i.t('reviews:fields.order')}>
                                    <a href={`/shop/orders/${review.orderId}`} target="_blank" rel="noreferrer">#{review.orderId}</a>
                                </MetaRow>
                            )}

                            <MetaRow icon={<ClockCircleOutlined />} label={i.t('reviews:fields.date')}>
                                {formatDateTime(review.createdAt)}
                            </MetaRow>

                            {review.moderatedAt && (
                                <MetaRow icon={<SafetyCertificateOutlined />} label={i.t('reviews:fields.moderatedAt')}>
                                    {formatDateTime(review.moderatedAt)}
                                </MetaRow>
                            )}

                            {review.systemInfo && (
                                <MetaRow icon={<MobileOutlined />} label={i.t('def:device_info')}>
                                    {review.systemInfo}
                                </MetaRow>
                            )}
                        </div>
                    </div>
                </Col>

                {revision && (
                    <Col span={24}>
                        <div className="order__block" style={{ width: '100%', height: 'auto' }}>
                            <SectionBadge
                                icon={<EditOutlined />}
                                text={i.t('reviews:revision.title')}
                                background="linear-gradient(135deg, #8a5310, #c08234)"
                            />

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
                                <Alert type="info" showIcon message={i.t('reviews:revision.hint')} />

                                <Row gutter={[16, 16]}>
                                    <Col span={24} md={12}>
                                        <FieldBlock label={i.t('reviews:revision.was')}>
                                            <RatingStars value={review.rating} size={16} />
                                            <div style={{ marginTop: 6, color: review.comment ? 'var(--text-color)' : 'var(--text-placeholder-color)' }}>
                                                {review.comment ?? i.t('reviews:noComment')}
                                            </div>
                                        </FieldBlock>
                                    </Col>
                                    <Col span={24} md={12}>
                                        <FieldBlock label={i.t('reviews:revision.becomes')}>
                                            <RatingStars value={revision.rating} size={16} />
                                            <div style={{ marginTop: 6, color: revision.comment ? 'var(--text-color)' : 'var(--text-placeholder-color)' }}>
                                                {revision.comment ?? i.t('reviews:noComment')}
                                            </div>
                                        </FieldBlock>
                                    </Col>
                                </Row>

                                <Photos files={revision.files} />
                            </div>
                        </div>
                    </Col>
                )}

                <Col span={24} xl={14}>
                    <div className="order__block" style={{ width: '100%', height: 'auto', alignSelf: 'flex-start' }}>
                        <SectionBadge
                            icon={<MessageOutlined />}
                            text={i.t('reviews:reply.title')}
                            background="linear-gradient(135deg, #2f6f5e, #4c9b85)"
                        />

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
                            <Input.TextArea
                                rows={4}
                                value={controller.reply}
                                placeholder={i.t('reviews:reply.placeholder')}
                                onChange={e => controller.set('reply', e.target.value)}
                            />
                            <div style={{ fontSize: 12, color: 'var(--text-muted-color)' }}>{i.t('reviews:reply.hint')}</div>

                            <Space>
                                <Button type="primary" icon={<MessageOutlined />} onClick={() => controller.saveReply(review.id)}>
                                    {i.t('def:actions.save')}
                                </Button>
                                {review.replyAt && (
                                    <span style={{ fontSize: 12, color: 'var(--text-muted-color)' }}>
                                        {i.t('reviews:reply.answeredAt')}: {formatDateTime(review.replyAt)}
                                    </span>
                                )}
                            </Space>
                        </div>
                    </div>
                </Col>

                <Col span={24} xl={10}>
                    <div className="order__block" style={{ width: '100%', height: 'auto', alignSelf: 'flex-start' }}>
                        <SectionBadge
                            icon={<SafetyCertificateOutlined />}
                            text={i.t('reviews:moderation.title')}
                            background="linear-gradient(135deg, #34526e, #567f9e)"
                        />

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
                            <PlainField label={i.t('reviews:moderation.reason')}>
                                <Input.TextArea
                                    rows={3}
                                    value={controller.rejectReason}
                                    placeholder={i.t('reviews:moderation.reasonPlaceholder')}
                                    onChange={e => controller.set('rejectReason', e.target.value)}
                                />
                            </PlainField>

                            <Space wrap>
                                <Button
                                    type="primary"
                                    icon={<CheckOutlined />}
                                    onClick={() => controller.moderate([review.id], 'approve', 'one')}
                                >
                                    {revision ? i.t('reviews:moderation.approveRevision') : i.t('reviews:actions.approve')}
                                </Button>
                                <Button
                                    danger
                                    icon={<CloseOutlined />}
                                    onClick={() => controller.moderate([review.id], 'reject', 'one')}
                                >
                                    {revision ? i.t('reviews:moderation.rejectRevision') : i.t('reviews:actions.reject')}
                                </Button>
                            </Space>
                        </div>
                    </div>
                </Col>
            </Row>
        </>
    )
})
